import { IVerticalBarItem, PermissionTypes, SDK_CONSTANTS } from 'fso-fincrime-sdk-ui';

import ActiveIcon from '../../assets/icons/Active.svg';
import InactiveIcon from '../../assets/icons/Inactive.svg';
import { IGraph } from '../../ts/entities/chart/IChart';

const URL_PARAMS = {
  CASE_CREATION: 'caseCreation',
};

const ATTRIBUTES = {
  RISK_SCORE_VAL: 'riskScoreValue',
  RISK_SCORE_RANK: 'riskScoreRank',
  RISK_SCORE_COMBINED: 'riskScoreCombined',
  FOCAL_ENTITY_NAME: 'focalEntity',
  TASK_DUE_DATE: 'dueDate',
  CREATED_DATE: 'createdDate',
  ASSIGNED_DATE: 'assignedOn',
  SLA_STATUS: 'slaStatus',
  CASE_ID: 'caseId',
  CASE_DISPLAY_ID: 'caseDisplayId',
  TASK_STATUS: 'taskStatus',
  ASSIGNED_TO: 'assignee',
  CASE_TAGS: 'caseTags',
  PROCESS_BAR: 'processBar',
  BLANK: 'blank',
  TIMER_END_DATE: 'timerEndDate',
};

const TOP_VERTICAL_BAR_ITEMS: IVerticalBarItem[] = [
  {
    label: 'DASHBOARD',
    icon: 'dashboard',
    url: SDK_CONSTANTS.PAGES_URL.INBOX,
    internalUrl: true,
    isToOpenOnANewTab: false,
  },
  {
    label: 'ALL_CASES',
    icon: 'work',
    url: SDK_CONSTANTS.PAGES_URL.CASE_LIST,
    internalUrl: true,
    permissions: [PermissionTypes.CASE_LIST],
    isToOpenOnANewTab: false,
  },
  {
    label: 'ABOUT_PAGE',
    icon: 'info',
    url: SDK_CONSTANTS.PAGES_URL.ABOUT_PAGE,
    internalUrl: true,
    permissions: [PermissionTypes.ABOUT_PAGE],
    isToOpenOnANewTab: false,
  },
];

const BOTTOM_VERTICAL_BAR_ITEMS: IVerticalBarItem[] = [
  {
    label: 'CONFIGURATIONS',
    icon: 'admin_panel_settings',
    url: SDK_CONSTANTS.PAGES_URL.CONFIG_PAGE,
    internalUrl: true,
    permissions: [PermissionTypes.CONFIG_PAGE],
    isToOpenOnANewTab: false,
  },
];

const DOC_CENTER = {
  UPLOAD_DOC_COLUMN: [
    { label: 'DOC_NAME', value: 'documentName' },
    { label: 'FILE_NAME', value: '' },
    { label: 'FILE_TYPE', value: 'documentType' },
    { label: 'VALID_DATE', value: 'expirationDate' },
    { label: 'VIRUS_SCAN', value: '' },
    { label: '', value: '' },
    { label: '', value: '' },
  ],
  EDIT_DOC_COLUMN: [
    { label: 'DOC_NAME', value: 'documentName' },
    { label: 'FILE_NAME', value: '' },
    { label: 'FILE_TYPE', value: 'documentType' },
    { label: 'VALID_DATE', value: 'expirationDate' },
    { label: 'DOC_CENTER.DOCUMENT_ACTIVE', value: 'documentActive' },
    { label: '', value: '' },
    { label: '', value: '' },
  ],
  FILE_SECTION_COLS: [
    { label: '', size: 1 },
    { label: 'FILE_NAME', size: 2 },
    { label: 'FILE_TYPE', size: 2 },
    { label: 'LAST_UPDATED_DATE', size: 2 },
    { label: 'VALID_DATE', size: 2 },
    { label: '', size: 3 },
  ],
  DOC_TYPE: 'Document', // TODO - this needs to be configurable
  DOC_NAME_MAX_LENGTH: 50,
  DECIMALS: 2,
  BYTE_TRESHOLD: 1024,
  TABLE: {
    COLUMNS: {
      ACTION_BUTTONS: 'documentActionButton',
      DOCUMENT_DOWNLOAD: 'documentOriginalName',
    },
  },
};

// By color ordered by tint
const CHART_COLORS = {
  MAROON: ['#C981B2', '#B14891', '#922B73', '#750E5C', '#5A0A42', '#42152D', '#351C21'],
  RED: ['#FF9A91', '#FF736A', '#F95D54', '#FF4136', '#E0362C', '#B9251C', '#7A130D'],
  ORANGE: ['#FFB46A', '#FF9831', '#FF810A', '#FF6D00', '#F76900', '#EB4F00', '#BC2F00'],
  GREEN: ['#8CE8AD', '#57E188', '#34C768', '#2DB757', '#189D3E', '#168736', '#13652A'],
  TEAL: ['#93F0E6', '#60E6E1', '#42C9C2', '#27ACAA', '#109090', '#0D7575', '#004F4F'],
  BLUE: ['#87D3F2', '#4EBEEB', '#35A4E8', '#188CE5', '#1777CF', '#155CB4', '#082C65'],
  PURPLE: ['#9C82D4', '#724BC3', '#542EA5', '#3D108A', '#240D75', '#0A095A', '#15173E'],
};

const ICONS = {
  ACTIVE_ICON: ActiveIcon,
  DEACTIVE_ICON: InactiveIcon,
};

const EXPORT_OPTIONS = [
  { key: 'JSON', permissionType: PermissionTypes.DOWNLOAD_JSON },
  { key: 'EXCEL', permissionType: PermissionTypes.DOWNLOAD_EXCEL },
  { key: 'PDF', permissionType: PermissionTypes.DOWNLOAD_PDF },
];

const CHART = {
  CHART_COLORS,
  RANGE: 'RANGE',
  REQUEST: [
    {
      chartType: 'bar',
      stacked: false,
      dataset: 'Cases By Status',
    },
    {
      chartType: 'bar',
      stacked: true,
      dataset: 'Tasks By Status',
    },
  ] as IGraph[], // TODO: This is static for now, in future should come from configurations
};

const SLA = {
  ON_TRACK: { label: 'SLA.ON_TRACK', value: 'ON_TRACK' },
  AT_RISK: { label: 'SLA.AT_RISK', value: 'AT_RISK' },
  OFF_TRACK: { label: 'SLA.OFF_TRACK', value: 'OFF_TRACK' },
};

const SLA_STATUS = {
  [SLA.ON_TRACK.value]: {
    className: 'color-sla-on-track',
    label: SLA.ON_TRACK.label,
    labelKey: 'ON_TRACK',
  },
  [SLA.OFF_TRACK.value]: {
    className: 'color-sla-off-track',
    label: SLA.OFF_TRACK.label,
    labelKey: 'OFF_TRACK',
  },
  [SLA.AT_RISK.value]: {
    className: 'color-sla-at-risk',
    label: SLA.AT_RISK.label,
    labelKey: 'AT_RISK',
  },
};

const TASK_INTERNAL_STATUS = {
  COMPLETED: 'COMPLETED',
  CANCELED: 'CANCELED',
};

const NOTIFICATIONS = {
  INFINITE_SCROLL_THRESHOLD: 0.4,
};

const RFI = {
  RFI_TABLE: ['DATE_CREATE', 'EMAIL', 'CREATED_BY', 'TYPE', 'STATUS'],
  TEMPLATE_TYPE: 'Email',
  DRAFT_NAME_LENGTH: 100,
  EMAIL_REGEX:
    /^[a-zA-Z0-9_]+([.-][a-zA-Z0-9_]+)*@[a-zA-Z0-9_]+([.-][a-zA-Z0-9_]+)*\.[a-zA-Z]{2,3}$/,
};

const CASE_LINK = {
  PARENT: 'Parent',
  CHILD: 'Child',
  RELATED: 'Related',
};

export const CONSTANTS = {
  NON_EXISTENT: 'Non Existent',
  ATTRIBUTES,
  VERTICAL_BAR_ITEMS: {
    TOP: TOP_VERTICAL_BAR_ITEMS,
    BOTTOM: BOTTOM_VERTICAL_BAR_ITEMS,
  },
  TEXT_FIELD_LENGTH: 996,

  SLA,
  QUESTION_VALUE_SEPARATOR: '|',
  IDENTIFICATIONSTATUS: {
    NOT_REQUIRED: 'Not_Required',
    NOT_IDENTIFIED: 'Not_Identified',
    IDENTIFIED: 'Identified',
  },
  VERIFICATIONSTATUS: {
    NOT_REQUIRED: 'Not_Required',
    NOT_VERIFIED: 'Not_Verified',
    VERIFIED: 'Verified',
    PARTIALLY_VERIFIED: 'Partially_Verified',
  },
  ICONS,
  CHART,
  DOC_CENTER,
  EXPORT_OPTIONS,
  FILE_SIZE: ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
  DATE_FORMATE: 'DDMMYYYY',
  MASTER: 'MASTER',
  DRAWER_WIDTH: 240,
  NULL_EPOCH: 0,
  SLA_STATUS,
  TASK_INTERNAL_STATUS,
  URL_PARAMS,
  NOTIFICATIONS,
  MAX_TABLE_LENGTH_FOR_AUTO_HEIGHT: 10,
  RFI,
  CASE_LINK,
  TASK_LIST: 'TASK_LIST',
  TASK: 'TASK',
  CASE_LIST: 'CASE_LIST',
} as const;
